/* global BigInt */
// Quantity math only. Prices, costs and valuation keep the commercial unit.
function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return a < 0n ? -a : a;
}

function ratio(n, d = 1n) {
  const common = gcd(n, d);
  return { n: n / common, d: d / common };
}

function decimal(value) {
  const text = String(value ?? "").trim().replace(",", ".");
  if (text.length > 128) return null;
  const parts = /^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(text);
  if (!parts || !(parts[2] || parts[3]) || Math.abs(Number(parts[4] || 0)) > 100) return null;
  const places = (parts[3] || "").length - Number(parts[4] || 0);
  const n = BigInt((parts[1] === "-" ? "-" : "") + (parts[2] || "0") + (parts[3] || ""));
  return places >= 0 ? ratio(n, 10n ** BigInt(places)) : ratio(n * 10n ** BigInt(-places));
}

function numberFraction(value) {
  const parts = String(value ?? "").split("/");
  if (parts.length === 1) return decimal(value);
  const a = decimal(parts[0]), b = decimal(parts[1]);
  return parts.length === 2 && a && b?.n > 0n ? ratio(a.n * b.d, a.d * b.n) : null;
}

const nearest = (n, d) => n < 0n ? -nearest(-n, d) : (2n * n + d) / (2n * d);

function projected(n, d, places) {
  const v = nearest(n * 10n ** BigInt(places), d);
  const unsigned = String(v < 0n ? -v : v).padStart(places + 1, "0");
  if (!places) return String(v);
  const tail = unsigned.slice(-places).replace(/0+$/, "");
  return (v < 0n ? "-" : "") + unsigned.slice(0, -places) + (tail ? "." + tail : "");
}

export function inventoryBasis(item) {
  if (!item) return null;
  if (item.physical_basis) return item.physical_basis;
  const unit = (level) => item[level] || item.units?.find((u) => u.level === level);
  const id = (level) => Number(item[level + "_id"] ?? unit(level)?.id) || null;
  const primaryId = id("unit"), childId = id("subunit"), lastId = id("subunit2");
  const first = decimal(item.pack_size ?? unit("subunit")?.factor);
  if (!primaryId || !childId || !first || first.n <= 0n) return null;
  let total = first;
  if (lastId) {
    const second = decimal(item.subunit2_size);
    total = second ? ratio(first.n * second.n, first.d * second.d) : decimal(unit("subunit2")?.factor);
    if (!total || total.n <= 0n) return null;
  }
  return { level: lastId ? "subunit2" : "subunit", primary_unit_id: primaryId,
    unit_id: lastId || childId, factor_num: String(total.n), factor_den: String(total.d),
    subunit_id: childId, subunit_factor_num: String(first.n), subunit_factor_den: String(first.d) };
}

function stepsPerUnit(basis, level) {
  if (level === basis.level) return ratio(1n);
  const n = BigInt(basis.factor_num), d = BigInt(basis.factor_den);
  if (level === "unit") return { n, d };
  return level === "subunit" && basis.subunit_factor_num
    ? ratio(n * BigInt(basis.subunit_factor_den), d * BigInt(basis.subunit_factor_num)) : null;
}

export function exactInput(value, level, basis) {
  const quantity = numberFraction(value);
  const factor = basis && stepsPerUnit(basis, level);
  if (!quantity || !factor) return null;
  const n = quantity.n * factor.n, d = quantity.d * factor.d;
  return n % d === 0n ? { ...basis, steps: String(n / d) } : null;
}

// Compatibility for persisted DECIMAL(20,8) snapshots only. Never use this to
// turn a new commercial quantity into an integer subunit.
export function historicalExact(baseQty, basis) {
  const quantity = decimal(baseQty);
  if (!quantity || !basis) return null;
  const factor = stepsPerUnit(basis, "unit");
  const step = nearest(quantity.n * factor.n, quantity.d * factor.d);
  const scale = 100000000n;
  const error = (s) => nearest(s * factor.d * scale, factor.n) * quantity.d - quantity.n * scale;
  const equals = (s) => error(s) === 0n;
  if (equals(step) && !equals(step - 1n) && !equals(step + 1n)) return { ...basis, steps: String(step) };
  return null;
}

export function quantityFromExact(exact, level, places = 8) {
  if (!exact || exact.steps == null) return null;
  const factor = stepsPerUnit(exact, level);
  return factor ? projected(BigInt(exact.steps) * factor.d, factor.n, places) : null;
}

export function displayQuantity(value, level, basis, exact) {
  if (value == null || value === "" || !basis) return value;
  if (exact?.steps != null) return quantityFromExact(exact, level, 4);
  const physical = exactInput(value, level, basis) || (level === "unit" ? historicalExact(value, basis) : null);
  if (physical) return quantityFromExact(physical, level, 4);
  // A calculated Number may represent a periodic commercial equivalent. Match
  // its Number representation for display without changing the saved quantity.
  if (typeof value === "number" && level !== basis.level && Number.isFinite(value)) {
    const factor = stepsPerUnit(basis, level);
    const steps = factor && Math.round(value * Number(factor.n) / Number(factor.d));
    if (Number.isSafeInteger(steps)) {
      const candidate = { ...basis, steps: String(steps) };
      if (Number(quantityFromExact(candidate, level, 24)) === value) return quantityFromExact(candidate, level, 4);
    }
  }
  return value;
}

export function stockInUnit(baseQty, level, basis, exact, fallbackFactor = 1) {
  if (baseQty == null) return null;
  const physical = exact?.steps != null ? exact : historicalExact(baseQty, basis);
  return quantityFromExact(physical, level, 24) ?? Number(baseQty) * fallbackFactor;
}

// Preserve the integer part for physical quantities beyond JavaScript Number precision.
export function formatExactQuantity(exact, level, languageId, signed = false) {
  const value = quantityFromExact(exact, level, 4);
  if (value == null) return null;
  const negative = BigInt(exact.steps) < 0n;
  const positive = BigInt(exact.steps) > 0n;
  const prefix = negative ? "-" : signed && positive ? "+" : "";
  const locale = languageId === "es" || languageId === "ca" ? "es-ES" : "en-GB";
  const separator = locale === "es-ES" ? "," : ".";
  if (value === "0" && (positive || negative)) return (negative ? ">−" : "<") + "0" + separator + "0001";
  const [whole, fraction] = value.replace("-", "").split(".");
  return prefix + BigInt(whole).toLocaleString(locale) + (fraction ? separator + fraction : "");
}
